<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useAdminEditsStore, type EditRequest } from '@/stores/adminEdits';
import { useVillagesStore } from '@/stores/villages';
import {
  CheckIcon,
  XIcon,
  TrashIcon,
  ClipboardCopyIcon,
  ShieldCheckIcon,
  PhoneIcon,
  UserIcon,
  BuildingIcon,
  CalendarIcon,
  ArrowRightIcon,
  ClockIcon,
  RefreshIcon,
} from 'vue-tabler-icons';

const props = defineProps<{
  modelValue: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
}>();

const adminEditsStore = useAdminEditsStore();
const villagesStore = useVillagesStore();

const activeTab = ref<'PENDING' | 'APPROVED' | 'REJECTED'>('PENDING');
const copiedId = ref<string | null>(null);
const actionNotice = ref<{ text: string; color: string } | null>(null);

// Only sync Sheet 2 when Admin opens this dialog
watch(
  () => props.modelValue,
  (isOpen) => {
    if (isOpen) {
      adminEditsStore.syncFromSheet();
    }
  },
  { immediate: true }
);

const filteredRequests = computed(() => {
  return adminEditsStore.pendingRequests.filter((r) => r.status === activeTab.value);
});

function closeDialog() {
  emit('update:modelValue', false);
}

async function handleApprove(req: EditRequest) {
  const result = await adminEditsStore.approveEditRequest(req.id);
  if (result.success) {
    villagesStore.applyMemberUpdate(
      req.villageId,
      req.familyId,
      req.memberId,
      req.updatedData
    );
    if (result.sheetUpdated) {
      actionNotice.value = {
        text: `✅ "${req.memberName}" ની વિગતો મંજૂર થઈ અને મુખ્ય શીટ (Sheet 1) માં સફળતાપૂર્વક અપડેટ થઈ ગઈ!`,
        color: 'success',
      };
    } else {
      actionNotice.value = {
        text: `⚠️ વિનંતી મંજૂર થઈ, પરંતુ Google Sheet માં સેવ નથી થઈ કારણ કે .env માં VITE_GOOGLE_APPS_SCRIPT_URL હજી placeholder છે! Apps Script Deploy કરી તેનો સાચો URL .env માં ઉમેરો.`,
        color: 'warning',
      };
    }
    setTimeout(() => {
      actionNotice.value = null;
    }, 7000);
  }
}

function handleReject(req: EditRequest) {
  adminEditsStore.rejectEditRequest(req.id);
  actionNotice.value = {
    text: `❌ "${req.memberName}" ની વિનંતી નામંજૂર કરવામાં આવી.`,
    color: 'error',
  };
  setTimeout(() => {
    actionNotice.value = null;
  }, 3500);
}

function handleDelete(req: EditRequest) {
  adminEditsStore.deleteRequest(req.id);
}

function copySheetRow(req: EditRequest) {
  // Format for easy pasting into Google Sheets
  const u = req.updatedData;
  const rowText = [
    req.villageName,
    req.familyId,
    u.name || req.originalData.name || '',
    u.nameE || req.originalData.nameE || '',
    u.age || req.originalData.age || '',
    u.sex || req.originalData.sex || '',
    u.role || req.originalData.role || '',
    u.occupation || req.originalData.occupation || '',
    u.education || req.originalData.education || '',
    u.maternal || req.originalData.maternal || '',
    u.dob || req.originalData.dob || '',
    u.currentResidence || req.originalData.currentResidence || '',
    u.contact || req.originalData.contact || '',
  ].join('\t');

  navigator.clipboard.writeText(rowText);
  copiedId.value = req.id;
  setTimeout(() => {
    copiedId.value = null;
  }, 2000);
}
</script>

<template>
  <v-dialog
    :model-value="modelValue"
    max-width="850"
    scrollable
    @update:model-value="(val) => emit('update:modelValue', val)"
  >
    <v-card class="rounded-xl overflow-hidden shadow-24">
      <!-- Header -->
      <v-card-item class="bg-primary text-white py-3 px-4">
        <div class="d-flex align-center justify-space-between w-100">
          <div class="d-flex align-center" style="gap: 10px;">
            <v-avatar size="34" color="white" class="text-primary font-weight-bold">
              <ShieldCheckIcon size="18" class="text-primary" />
            </v-avatar>
            <div>
              <div class="text-subtitle-1 font-weight-black line-height-1">
                એડમિન વેરિફિકેશન પેનલ (Admin Verification)
              </div>
              <div class="text-caption text-white opacity-80">
                સુધારાની વિનંતીઓ ચકાસો અને મંજૂર કરો
              </div>
            </div>
          </div>

          <v-btn icon size="small" variant="text" color="white" @click="closeDialog">
            <XIcon size="18" />
          </v-btn>
        </div>
      </v-card-item>

      <!-- Tabs & Sync Button -->
      <div class="px-4 pt-3 border-b bg-light d-flex align-center justify-space-between flex-wrap" style="gap: 8px;">
        <v-btn-toggle
          v-model="activeTab"
          mandatory
          density="compact"
          color="primary"
          variant="outlined"
          class="mb-2"
        >
          <v-btn value="PENDING" size="small" class="font-weight-bold">
            બાકી વિનંતી ({{ adminEditsStore.pendingCount }})
          </v-btn>
          <v-btn value="APPROVED" size="small" class="font-weight-bold">
            મંજૂર થયેલ (Approved)
          </v-btn>
          <v-btn value="REJECTED" size="small" class="font-weight-bold">
            અસ્વીકાર (Rejected)
          </v-btn>
        </v-btn-toggle>

        <v-btn
          size="small"
          variant="tonal"
          color="primary"
          rounded="lg"
          class="mb-2 font-weight-medium"
          :loading="adminEditsStore.isSyncing"
          @click="adminEditsStore.syncFromSheet()"
        >
          <RefreshIcon size="14" class="mr-1" />
          Sheet થી સિંક
        </v-btn>
      </div>

      <!-- Apps Script Configuration Warning -->
      <div v-if="!adminEditsStore.isAppsScriptConfigured()" class="px-4 pt-3">
        <v-alert
          type="warning"
          density="compact"
          variant="tonal"
          class="rounded-lg text-caption font-weight-medium"
        >
          <strong>ગૂગલ શીટ કનેક્શન બાકી છે:</strong> મુખ્ય શીટ (Sheet 1) માં ઓટોમેટિક સેવ થવા માટે <code>.env</code> માં <code>VITE_GOOGLE_APPS_SCRIPT_URL</code> મુકવો જરૂરી છે (હજી placeholder URL છે).
        </v-alert>
      </div>

      <!-- Live Action Feedback Alert -->
      <div v-if="actionNotice" class="px-4 pt-3">
        <v-alert
          :color="actionNotice.color"
          density="compact"
          variant="tonal"
          class="rounded-lg text-caption font-weight-bold"
        >
          {{ actionNotice.text }}
        </v-alert>
      </div>

      <!-- Requests List -->
      <v-card-text class="pa-4 pa-sm-5" style="max-height: 600px;">
        <div v-if="filteredRequests.length === 0" class="text-center py-8">
          <ClockIcon size="42" class="text-medium-emphasis mb-2" />
          <div class="text-subtitle-2 font-weight-bold text-medium-emphasis">
            આ કેટેગરીમાં કોઈ વિનંતીઓ નથી.
          </div>
        </div>

        <div v-else class="d-flex flex-column" style="gap: 14px;">
          <v-card
            v-for="req in filteredRequests"
            :key="req.id"
            elevation="0"
            variant="outlined"
            class="pa-3 pa-sm-4 rounded-xl border position-relative"
          >
            <!-- Top Row: Village, Family, Member -->
            <div class="d-flex align-start justify-space-between flex-wrap mb-2" style="gap: 8px;">
              <div>
                <div class="d-flex align-center flex-wrap" style="gap: 6px;">
                  <span class="text-subtitle-2 font-weight-black text-primary">
                    {{ req.memberName }}
                  </span>
                  <v-chip size="x-small" color="secondary" variant="tonal" class="font-weight-bold">
                    <BuildingIcon size="12" class="mr-1" />
                    {{ req.villageName }}
                  </v-chip>
                  <span class="text-caption text-medium-emphasis">
                    (કુટુંબ: {{ req.headName }})
                  </span>
                </div>

                <div class="text-caption text-medium-emphasis mt-1">
                  વિનંતી કરનાર: <strong>{{ req.submitterName }}</strong>
                  <span v-if="req.submitterPhone" class="ml-2">
                    (<PhoneIcon size="12" class="d-inline mr-1" />{{ req.submitterPhone }})
                  </span>
                  <span class="ml-2 text-disabled">• {{ req.timestamp }}</span>
                </div>
              </div>

              <!-- Status Badge -->
              <div>
                <v-chip
                  v-if="req.status === 'PENDING'"
                  color="warning"
                  size="small"
                  variant="flat"
                  class="font-weight-bold"
                >
                  બાકી (Pending)
                </v-chip>
                <v-chip
                  v-else-if="req.status === 'APPROVED'"
                  color="success"
                  size="small"
                  variant="flat"
                  class="font-weight-bold"
                >
                  મંજૂર (Approved)
                </v-chip>
                <v-chip
                  v-else
                  color="error"
                  size="small"
                  variant="flat"
                  class="font-weight-bold"
                >
                  અસ્વીકાર (Rejected)
                </v-chip>
              </div>
            </div>

            <!-- Note if any -->
            <div
              v-if="req.note"
              class="pa-2 rounded-lg bg-light text-caption mb-3 text-medium-emphasis border"
            >
              <strong>નોંધ:</strong> {{ req.note }}
            </div>

            <!-- Diffs Table -->
            <div class="mb-3">
              <div class="text-caption font-weight-bold text-medium-emphasis mb-1">
                સૂચવેલા ફેરફારો:
              </div>
              <v-table density="compact" class="rounded-lg border">
                <thead>
                  <tr class="bg-light">
                    <th class="text-left py-1 text-caption font-weight-bold" style="width: 25%;">વિગત</th>
                    <th class="text-left py-1 text-caption font-weight-bold" style="width: 35%;">હાલનું મૂલ્ય (Old)</th>
                    <th class="text-left py-1 text-caption font-weight-bold" style="width: 40%;">નવું મૂલ્ય (New)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(diff, idx) in req.diffs" :key="idx">
                    <td class="text-caption font-weight-bold text-primary py-1">
                      {{ diff.label }}
                    </td>
                    <td class="text-caption text-medium-emphasis py-1 text-decoration-line-through">
                      {{ diff.oldVal || '(ખાલી)' }}
                    </td>
                    <td class="text-caption font-weight-bold text-success py-1">
                      {{ diff.newVal || '(ખાલી)' }}
                    </td>
                  </tr>
                </tbody>
              </v-table>
            </div>

            <!-- Action Buttons for Admin -->
            <div class="d-flex align-center justify-space-between flex-wrap pt-2 border-t" style="gap: 8px;">
              <v-btn
                size="small"
                variant="tonal"
                color="secondary"
                rounded="lg"
                title="Google Sheet માં પેસ્ટ કરવા માટે કોપી કરો"
                @click="copySheetRow(req)"
              >
                <CheckIcon v-if="copiedId === req.id" size="14" class="text-success mr-1" />
                <ClipboardCopyIcon v-else size="14" class="mr-1" />
                {{ copiedId === req.id ? 'કોપી થયું!' : 'Sheet લાઇન કોપી' }}
              </v-btn>

              <div class="d-flex align-center" style="gap: 6px;">
                <template v-if="req.status === 'PENDING'">
                  <v-btn
                    size="small"
                    color="success"
                    variant="flat"
                    rounded="lg"
                    class="font-weight-bold"
                    @click="handleApprove(req)"
                  >
                    <CheckIcon size="14" class="mr-1" />
                    મંજૂર કરો (Approve)
                  </v-btn>
                  <v-btn
                    size="small"
                    color="error"
                    variant="tonal"
                    rounded="lg"
                    @click="handleReject(req)"
                  >
                    <XIcon size="14" class="mr-1" />
                    અસ્વીકાર
                  </v-btn>
                </template>

                <v-btn
                  icon
                  size="x-small"
                  variant="text"
                  color="secondary"
                  title="ડિલીટ કરો"
                  @click="handleDelete(req)"
                >
                  <TrashIcon size="15" />
                </v-btn>
              </div>
            </div>
          </v-card>
        </div>
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-3 d-flex justify-end">
        <v-btn color="secondary" variant="tonal" rounded="lg" @click="closeDialog">
          બંધ કરો
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
